import { useEffect, useRef } from "react";

import { getLicenseUrgency, urgencyLabel } from "../utils/licenseUrgency";

/**
 * Fires a persistent toast when the license isn't healthy and
 * auto-dismisses it when the license recovers (e.g. after the admin
 * uploads a renewal — the LicenseProvider's poll picks up the new
 * state within a tick).
 *
 * Replaces the old top-bar banner. Properties:
 *   - Persistent (`duration: 0`) — stays until either (a) the license
 *     becomes healthy (auto-dismiss) or (b) the user clicks the X.
 *   - De-duped via a stable key so re-renders don't stack toasts.
 *   - Hidden in cloud SaaS (`licensingEnabled === false`) and while the
 *     initial fetch is in flight (`loading === true`).
 *   - Hidden when the license is healthy (mode === "licensed" && days > 30).
 *   - Re-fires after recovery → degradation: if a previously-shown
 *     toast was auto-dismissed because the license became healthy, a
 *     fresh expiry transition will fire a new one.
 *
 * The action button calls `onNavigate("platform-license")`.
 */
export function useLicenseToast({ license, notify, dismissToast, onNavigate }) {
  // Tracks the id of the currently-shown toast so we can dismiss it
  // when the license recovers. Null = no toast we own is on-screen.
  const activeToastIdRef = useRef(null);

  useEffect(() => {
    if (!license || !notify) return;
    if (!license.licensingEnabled) return;
    if (license.loading) return;

    const urgency = getLicenseUrgency(license);

    // Healthy → dismiss any toast we previously fired.
    if (urgency === "normal") {
      if (activeToastIdRef.current && dismissToast) {
        dismissToast(activeToastIdRef.current);
      }
      activeToastIdRef.current = null;
      return;
    }

    // Already showing a toast — don't stack another. The toast
    // copy/title don't depend on the granular urgency band, so we
    // don't need to refresh on soon→urgent transitions.
    if (activeToastIdRef.current) return;

    const isExpired = urgency === "expired";
    notify({
      type: isExpired ? "error" : "info",
      key: "license-state",
      title: urgencyLabel(license),
      message: isExpired
        ? "Live tag data, historian, and runtime pages are blocked until a valid license is loaded."
        : "Renew your gateway license before it expires to keep runtime endpoints available.",
      action: {
        label: isExpired ? "Renew now" : "View license",
        onClick: () => {
          activeToastIdRef.current = null;
          onNavigate?.("platform-license");
        },
      },
      duration: 0,
      // Capture the toast id so the recovery path above can dismiss it.
      onCreated: (id) => {
        activeToastIdRef.current = id;
      },
    });
  }, [license, notify, dismissToast, onNavigate]);
}

export default useLicenseToast;
